import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { formatCurrencyINR } from '../../../utils/currency';
import { generateId } from '../../../schemas/dataSchemas';

const DebtModal = ({ isOpen, onClose, onSave, editingLoan = null }) => {
  const [formData, setFormData] = useState({
    type: editingLoan?.type || 'Home Loans',
    name: editingLoan?.name || '',
    bank: editingLoan?.bank || '',
    account: editingLoan?.account || '',
    outstanding: editingLoan?.outstanding || '',
    monthlyEMI: editingLoan?.monthlyEMI || '',
    interestRate: editingLoan?.interestRate || '',
    tenure: editingLoan?.tenure || '',
    startDate: editingLoan?.startDate || new Date().toISOString().split('T')[0],
    prepayment: editingLoan?.prepayment || 0,
    notes: editingLoan?.notes || ''
  });

  const [errors, setErrors] = useState({});

  // Update form data when editingLoan changes
  useEffect(() => {
    if (editingLoan) {
      setFormData({
        type: editingLoan.type || 'Home Loans',
        name: editingLoan.name || '',
        bank: editingLoan.bank || '',
        account: editingLoan.account || '',
        outstanding: editingLoan.outstanding || '',
        monthlyEMI: editingLoan.monthlyEMI || '',
        interestRate: editingLoan.interestRate || '',
        tenure: editingLoan.tenure || '',
        startDate: editingLoan.startDate || new Date().toISOString().split('T')[0],
        prepayment: editingLoan.prepayment || 0,
        notes: editingLoan.notes || ''
      });
    } else {
      // Reset form when not editing
      setFormData({
        type: 'Home Loans',
        name: '',
        bank: '',
        account: '',
        outstanding: '',
        monthlyEMI: '',
        interestRate: '',
        tenure: '',
        startDate: new Date().toISOString().split('T')[0],
        prepayment: 0,
        notes: ''
      });
    }
    setErrors({});
  }, [editingLoan]);

  const loanTypeOptions = [
    { value: 'Home Loans', label: 'Home Loans' },
    { value: 'Car/Vehicle Loans', label: 'Car/Vehicle Loans' },
    { value: 'Personal Loans', label: 'Personal Loans' },
    { value: 'Education Loans', label: 'Education Loans' },
    { value: 'Credit Cards', label: 'Credit Cards' },
    { value: 'Business Loans', label: 'Business Loans' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type || formData.type === '') {
      newErrors.type = 'Loan type is required';
    }

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Loan name is required';
    }


    if (!formData.outstanding || formData.outstanding === '' || parseFloat(formData.outstanding) <= 0) {
      newErrors.outstanding = 'Outstanding amount is required and must be greater than 0';
    }
    
    // Validate outstanding is a valid number
    const outstanding = parseFloat(formData.outstanding);
    if (formData.outstanding && (isNaN(outstanding) || outstanding <= 0)) {
      newErrors.outstanding = 'Outstanding amount must be a valid positive number';
    }

    if (!formData.monthlyEMI || formData.monthlyEMI === '' || parseFloat(formData.monthlyEMI) <= 0) {
      newErrors.monthlyEMI = 'Monthly EMI is required and must be greater than 0';
    }
    
    // Validate monthly EMI is a valid number
    const monthlyEMI = parseFloat(formData.monthlyEMI);
    if (formData.monthlyEMI && (isNaN(monthlyEMI) || monthlyEMI <= 0)) {
      newErrors.monthlyEMI = 'Monthly EMI must be a valid positive number';
    }

    if (!formData.interestRate || formData.interestRate === '' || parseFloat(formData.interestRate) <= 0) {
      newErrors.interestRate = 'Interest rate is required and must be greater than 0';
    }
    
    // Validate interest rate is a valid number
    const interestRate = parseFloat(formData.interestRate);
    if (formData.interestRate && (isNaN(interestRate) || interestRate <= 0 || interestRate > 50)) {
      newErrors.interestRate = 'Interest rate must be between 0% and 50%';
    }

    if (!formData.tenure || formData.tenure === '' || parseInt(formData.tenure) <= 0) {
      newErrors.tenure = 'Tenure is required and must be greater than 0';
    }
    
    // Validate tenure is a valid number
    const tenure = parseInt(formData.tenure);
    if (formData.tenure && (isNaN(tenure) || tenure <= 0 || tenure > 50)) {
      newErrors.tenure = 'Tenure must be between 1 and 50 years';
    }

    if (!formData.startDate || formData.startDate === '') {
      newErrors.startDate = 'Start date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculatePayoffDate = () => {
    if (!formData.startDate || !formData.tenure) return '';
    
    const startDate = new Date(formData.startDate);
    const payoffDate = new Date(startDate);
    payoffDate.setFullYear(payoffDate.getFullYear() + parseInt(formData.tenure));
    
    return payoffDate.toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const calculateTotalInterest = () => {
    if (!formData.outstanding || !formData.interestRate || !formData.tenure) return 0;
    
    const principal = parseFloat(formData.outstanding);
    const rate = parseFloat(formData.interestRate) / 100;
    const years = parseInt(formData.tenure);
    
    // Simple interest calculation (approximate)
    return principal * rate * years;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const loanData = {
      id: editingLoan?.id || generateId(),
      type: formData.type,
      name: formData.name,
      bank: formData.bank,
      account: formData.account,
      outstanding: parseFloat(formData.outstanding),
      monthlyEMI: parseFloat(formData.monthlyEMI),
      interestRate: parseFloat(formData.interestRate),
      tenure: parseInt(formData.tenure),
      startDate: formData.startDate,
      payoffDate: calculatePayoffDate(),
      prepayment: parseFloat(formData.prepayment) || 0,
      totalInterest: calculateTotalInterest(),
      notes: formData.notes
    };

    onSave(loanData);
    onClose();
  };

  const handleClose = () => {
    setFormData({
      type: 'Home Loans',
      name: '',
      bank: '',
      account: '',
      outstanding: '',
      monthlyEMI: '',
      interestRate: '',
      tenure: '',
      startDate: new Date().toISOString().split('T')[0],
      prepayment: 0,
      notes: ''
    });
    setErrors({});
    onClose();
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="CreditCard" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {editingLoan ? 'Edit Loan' : 'Add New Loan'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {editingLoan ? 'Update loan details' : 'Enter loan information'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-destructive mb-1">
                    Please fix the following errors:
                  </h3>
                  <ul className="text-sm text-destructive space-y-1">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field} className="flex items-start gap-2">
                        <span className="text-destructive">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Loan Type"
              options={loanTypeOptions}
              value={formData.type}
              onChange={(value) => handleInputChange('type', value)}
              error={errors.type}
              required
            />

            <Input
              label="Loan Name"
              placeholder="e.g., Home Loan - Primary Residence"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={errors.name}
              required
            />

            <Input
              label="Bank/Institution"
              placeholder="e.g., HDFC Bank (optional)"
              value={formData.bank}
              onChange={(e) => handleInputChange('bank', e.target.value)}
              error={errors.bank}
            />

            <Input
              label="Account Number"
              placeholder="e.g., A/c XXXX1234 (optional)"
              value={formData.account}
              onChange={(e) => handleInputChange('account', e.target.value)}
              error={errors.account}
            />

            <Input
              label="Outstanding Amount (₹)"
              type="number"
              placeholder="5000000"
              value={formData.outstanding}
              onChange={(e) => handleInputChange('outstanding', e.target.value)}
              error={errors.outstanding}
              description="Current outstanding balance"
              required
            />

            <Input
              label="Monthly EMI (₹)"
              type="number"
              placeholder="45000"
              value={formData.monthlyEMI}
              onChange={(e) => handleInputChange('monthlyEMI', e.target.value)}
              error={errors.monthlyEMI}
              required
            />

            <Input
              label="Interest Rate (% p.a.)"
              type="number"
              step="0.1"
              placeholder="8.5"
              value={formData.interestRate}
              onChange={(e) => handleInputChange('interestRate', e.target.value)}
              error={errors.interestRate}
              required
            />

            <Input
              label="Tenure (Years)"
              type="number"
              placeholder="20"
              value={formData.tenure}
              onChange={(e) => handleInputChange('tenure', e.target.value)}
              error={errors.tenure}
              required
            />

            <Input
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              error={errors.startDate}
              required
            />

            <Input
              label="Prepayment Made (₹)"
              type="number"
              placeholder="0"
              value={formData.prepayment}
              onChange={(e) => handleInputChange('prepayment', e.target.value)}
              description="Any prepayments made so far"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Notes
            </label>
            <textarea
              placeholder="Additional notes about this loan..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Loan Preview */}
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-medium text-foreground mb-3">Loan Preview</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Payoff Date:</span>
                <span className="ml-2 font-medium">{calculatePayoffDate()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Interest:</span>
                <span className="ml-2 font-medium">{formatCurrencyINR(calculateTotalInterest())}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Remaining Balance:</span>
                <span className="ml-2 font-medium">
                  {formData.outstanding ? formatCurrencyINR(parseFloat(formData.outstanding) - parseFloat(formData.prepayment || 0)) : '₹0'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Monthly EMI:</span>
                <span className="ml-2 font-medium">
                  {formData.monthlyEMI ? formatCurrencyINR(parseFloat(formData.monthlyEMI)) : '₹0'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {editingLoan ? 'Update Loan' : 'Add Loan'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DebtModal;
